import { Bookmark, Clock } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Avatar } from "@/components/ui/avatar"
import { useToggleSaveRitual } from "@/features/learn-practice/hooks/useToggleSaveRitual"
import { cn } from "@/lib/utils"
import type { RitualToolResult } from "../../types/ai-chat.types"

const TYPE_LABELS: Record<string, string> = {
  IN_PERSON: "In Person",
  VIRTUAL_COURSE: "Virtual",
  SELF_GUIDED: "Self-Guided",
  SELF_GUIDED_COURSE: "Course",
}

function formatDuration(duration: string | null, durationType: string | null): string | null {
  if (!duration) return null
  const typeLabel = durationType === "HOURS" ? "hr" : durationType === "DAYS" ? "days" : "min"
  return `${duration} ${typeLabel}`
}

export function RitualRecommendationCard({ rituals }: { rituals: RitualToolResult[] }) {
  const navigate = useNavigate()
  const toggleSave = useToggleSaveRitual()

  if (rituals.length === 0) return null

  return (
    <div className="flex flex-col gap-2.5">
      {rituals.map(ritual => {
        const displayName = ritual.creatorName || ritual.communityName
        return (
          <button
            type="button"
            key={ritual.id}
            onClick={() => navigate(`/rituals/${ritual.id}`)}
            className={cn(
              "relative w-full h-[160px] overflow-hidden text-left cursor-pointer",
              "rounded-tl-[40px] rounded-br-[40px]",
              "transition-opacity hover:opacity-90",
            )}
          >
            {/* Cover image or dark gradient fallback */}
            {ritual.coverImage ? (
              <img
                src={ritual.coverImage}
                alt={ritual.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]" />
            )}

            {/* Top section: Creator/community info (left) + Bookmark (right) */}
            <div className="absolute top-4 left-5 right-5 z-10 flex items-start justify-between">
              {/* Creator or community info */}
              {displayName ? (
                <div
                  className="flex items-center gap-2 min-w-0"
                  onClick={e => {
                    e.stopPropagation()
                    if (ritual.creatorId) navigate(`/profile/${ritual.creatorId}`)
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter" && ritual.creatorId) {
                      e.stopPropagation()
                      navigate(`/profile/${ritual.creatorId}`)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Avatar
                    src={ritual.creatorAvatar || undefined}
                    fallback={displayName[0] || "?"}
                    size="lg"
                    className="w-[30px] h-[30px] shrink-0"
                  />
                  <span className="text-[13px] font-semibold text-primary opacity-90 leading-normal truncate">
                    {displayName}
                  </span>
                </div>
              ) : (
                <div />
              )}

              {/* Bookmark button -- always top-right */}
              <div
                className="shrink-0 cursor-pointer rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75 transition-colors hover:bg-[rgba(242,240,235,0.1)]"
                onClick={e => {
                  e.stopPropagation()
                  toggleSave.mutate(ritual.id)
                }}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.stopPropagation()
                    toggleSave.mutate(ritual.id)
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={ritual.isSaved ? "Remove bookmark" : "Add bookmark"}
              >
                <Bookmark
                  className={cn(
                    "w-[22px] h-[22px]",
                    ritual.isSaved ? "fill-white text-white" : "text-primary",
                  )}
                />
              </div>
            </div>

            {/* Bottom gradient overlay -- subtle, only behind text */}
            <div className="absolute inset-x-0 bottom-0 h-[70px] bg-gradient-to-t from-black/70 to-transparent z-10" />

            {/* Bottom content area */}
            <div className="absolute inset-x-0 bottom-0 px-5 pb-4 flex flex-col gap-1.5 z-20">
              {/* Title */}
              <h4 className="text-[18px] font-semibold text-primary opacity-90 leading-tight line-clamp-1">
                {ritual.title}
              </h4>

              {/* Metadata row */}
              <div className="flex items-center gap-2.5 text-[13px]">
                {/* Price badge */}
                <span
                  className={cn(
                    "rounded-[5px] px-1.5 py-0.5 text-[13px] font-medium leading-5 opacity-90",
                    ritual.isPaid
                      ? "bg-brand-terracotta-bright text-primary"
                      : "bg-olive text-primary",
                  )}
                >
                  {ritual.isPaid && ritual.fee
                    ? `${ritual.feeCurrency || "USD"}${Number(ritual.fee).toFixed(2)}`
                    : "Free"}
                </span>

                {/* Ritual type */}
                {ritual.ritualType && (
                  <span className="text-primary/70 leading-5">
                    {TYPE_LABELS[ritual.ritualType] || ritual.ritualType}
                  </span>
                )}

                {/* Duration */}
                {(() => {
                  const durationText = formatDuration(ritual.duration, ritual.durationType)
                  return durationText ? (
                    <span className="flex items-center gap-1 text-primary/70 leading-5">
                      <Clock className="h-3 w-3" />
                      {durationText}
                    </span>
                  ) : null
                })()}
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
